// Microbe RTS — How-to-Play tutorial (swipeable onboarding carousel).
//
// A self-contained overlay: a horizontal track of illustrated pages with dots + Back/Next + Skip,
// and a light swipe gesture (horizontal-intent lock so vertical scrolling of a long page still
// works). No sim coupling — it takes only a root element + a haptic fn. main.ts wires WHEN it opens
// (first-run before the intro, the intro's "How to play" button, and an in-game "?" that pauses).
//
// EVERY control + number below is verified against the sim/ui code (see the mechanics map), so the
// instructions match the real game exactly — a wrong "tap here" is worse than no tutorial.

type Haptic = (pattern: number | number[]) => void;

export interface Tutorial {
  open(): void;
  close(): void;
  readonly isOpen: boolean;
}

interface Page {
  icon: string;
  title: string;
  hook: string;
  body: string; // trusted static HTML (authored here, no user input)
}

// Titles/hooks are filled by main.ts from the Fable-written set if present; these are the fallbacks.
const PAGES: Page[] = [
  {
    icon: '🎯',
    title: 'Endure or Dissolve',
    hook: 'Break the rival before the oxygen tide rises.',
    body: `
      <p>You command a <b>microbial colony</b> on the ocean floor. Grow it, out-evolve the rival, and end them. Three paths to victory:</p>
      <ul class="tut-list">
        <li><span class="tut-k">⚔ Conquest</span> Smash the enemy <b>Home</b> crater to zero.</li>
        <li><span class="tut-k">🗺 Domination</span> Hold <b>8 of the 11</b> craters long enough.</li>
        <li><span class="tut-k">☁ Oxygenation</span> At <b>35:00</b> a toxic-oxygen tide poisons every Home. Hold the most territory (or evolve <b>Aerobic Respiration</b>) to outlast it.</li>
      </ul>`,
  },
  {
    icon: '👆',
    title: 'Command the Swarm',
    hook: 'Tap to move. Tap again to gather more.',
    body: `
      <ul class="tut-list">
        <li><span class="tut-k">Tap</span> one of your microbes to select it.</li>
        <li><span class="tut-k">Double-tap</span> it to grab all nearby microbes of the <b>same type</b>; <span class="tut-k">triple-tap</span> grabs <b>everything</b> nearby.</li>
        <li><span class="tut-k">Tap open water</span> with a group selected to send them there. Movement is tap-to-move.</li>
        <li><span class="tut-k">Pan</span> by dragging open water (nothing selected), or <b>press-and-hold ~½s then drag</b> when units are selected. <span class="tut-k">Pinch</span> to zoom.</li>
        <li><span class="tut-k">Deselect</span> via the “✕ N selected” pill, tapping a selected microbe again, or double-tapping open water.</li>
      </ul>`,
  },
  {
    icon: '👑',
    title: 'Found the Crater',
    hook: 'One drone, one crater. Settle. Crown your Queen.',
    body: `
      <p>You start as <b>one Drone</b>, no base yet.</p>
      <ul class="tut-list">
        <li>Swim out, find a glowing <b>home crater</b>, and move her onto it. Hold ~<b>6 seconds</b> to settle.</li>
        <li>Your <b>first colony is free</b>, and it crowns that drone your <b>Queen</b>: 3× health, a real fighter, self-healing.</li>
        <li>Tap the <span class="tut-k">⌖ crosshair</span> button anytime to snap the camera back home.</li>
      </ul>`,
  },
  {
    icon: '🗺️',
    title: 'Eyes in the Dark',
    hook: 'The right edge sees what the fog hides.',
    body: `
      <ul class="tut-list">
        <li>Top is the <b>sunlit surface</b>, bottom the <b>deep abyss</b>.</li>
        <li><span class="tut-k">Teal ●</span> yours · <span class="tut-k">red ▲</span> enemy · <span class="tut-k">grey ■</span> neutral · a <b>ring</b> = a home. Tiny dots are live microbes (enemies show only while you can see them).</li>
        <li><b>Dark</b> = unexplored, <b>dim</b> = seen before, <b>bright</b> = in sight now. The white box is your current view.</li>
        <li><span class="tut-k">Tap</span> (nothing selected) to glide your view · <span class="tut-k">drag</span> to fly there instantly · <span class="tut-k">tap with units selected</span> to send them marching.</li>
      </ul>`,
  },
  {
    icon: '🍽️',
    title: 'Feed the Colony',
    hook: 'Craters pay. Grazers bleed amino. Farm them early.',
    body: `
      <ul class="tut-list">
        <li>Three resources up top: <b>Energy</b> (plentiful), <b>Amino</b> (scarce, gates everything), <b>Toxin</b> (niche).</li>
        <li><b>No workers.</b> Simply <b>owning a crater pays you</b> every second. Grab reefs and vents to grow income.</li>
        <li>The wall: your Home trickles only ~<b>0.2 amino/s</b>, but a new colony costs <b>70 amino</b>.</li>
        <li><span class="tut-k">⭐ Farm Grazers</span> Slow 460-HP wild beasts at fixed lairs. <b>Focus-fire one</b> (tap it with a group selected) for <b>~230 amino</b>; it respawns in ~<b>38s</b>, so a lair is renewable food.</li>
        <li><span class="tut-k">⚠ Risk</span> A grazer bites for 17 and will <b>kill your lone drone</b>. Send your <b>Queen</b> to farm, and don’t wander into a lair carelessly.</li>
      </ul>`,
  },
  {
    icon: '🧬',
    title: 'Grow. Claim. Ascend.',
    hook: 'Spawn, seize craters, rewrite your genome, lay siege.',
    body: `
      <ul class="tut-list">
        <li><span class="tut-k">Tap your base</span> to open its panel: build units (each shows its cost, greyed if you can’t afford it or you’re <b>pop-capped</b>), set a <b>Rally</b> point, save <b>groups</b>.</li>
        <li><span class="tut-k">Claim</span> a neutral crater by moving a drone onto it (150⚡ / 70🧬 after the free first). A new outpost needs a <b>Brood</b> (80⚡/40🧬) before it can build.</li>
        <li><span class="tut-k">Siege</span> an enemy crater: smash its core to zero with <b>warriors</b> (tap the enemy node with warriors selected), then send a drone to claim it.</li>
        <li><span class="tut-k">EVOLVE</span> grows your genome: new units, tiers, keystones. The game <b>keeps running</b>, so hit pause (top-left) if you need to think.</li>
      </ul>`,
  },
];

export function mountTutorial(
  root: HTMLElement,
  haptic: Haptic,
  opts?: { onOpen?: () => void; onClose?: () => void; pages?: Page[] },
): Tutorial {
  const pages = opts?.pages ?? PAGES;
  const n = pages.length;
  let idx = 0;
  let open = false;

  // ---- build DOM ----
  root.classList.add('tut');
  root.hidden = true;

  const card = document.createElement('div');
  card.className = 'tut-card';

  const skip = document.createElement('button');
  skip.type = 'button';
  skip.className = 'tut-skip';
  skip.textContent = 'Skip';

  const viewport = document.createElement('div');
  viewport.className = 'tut-viewport';
  const track = document.createElement('div');
  track.className = 'tut-track';
  track.style.width = `${n * 100}%`;

  for (const p of pages) {
    const sec = document.createElement('section');
    sec.className = 'tut-page';
    sec.style.width = `${100 / n}%`;
    sec.innerHTML =
      `<div class="tut-icon">${p.icon}</div>` +
      `<h2 class="tut-title">${p.title}</h2>` +
      `<div class="tut-hook">${p.hook}</div>` +
      `<div class="tut-body">${p.body}</div>`;
    track.appendChild(sec);
  }
  viewport.appendChild(track);

  const dots = document.createElement('div');
  dots.className = 'tut-dots';
  for (let i = 0; i < n; i++) {
    const d = document.createElement('button');
    d.type = 'button';
    d.className = 'tut-dot';
    d.setAttribute('aria-label', `Page ${i + 1}`);
    d.addEventListener('click', () => go(i));
    dots.appendChild(d);
  }

  const nav = document.createElement('div');
  nav.className = 'tut-nav';
  const back = document.createElement('button');
  back.type = 'button';
  back.className = 'tut-back';
  back.textContent = 'Back';
  back.addEventListener('click', () => go(idx - 1));
  const next = document.createElement('button');
  next.type = 'button';
  next.className = 'tut-next';
  next.addEventListener('click', () => {
    if (idx >= n - 1) close();
    else go(idx + 1);
  });
  nav.append(back, next);

  card.append(skip, viewport, dots, nav);
  root.appendChild(card);
  skip.addEventListener('click', () => close());

  // ---- paging ----
  function sync(animate = true): void {
    track.style.transition = animate ? 'transform 0.28s cubic-bezier(.4,0,.2,1)' : 'none';
    track.style.transform = `translateX(${(-idx * 100) / n}%)`;
    back.disabled = idx === 0;
    next.textContent = idx >= n - 1 ? 'Play ▸' : 'Next';
    const dd = dots.children;
    for (let i = 0; i < dd.length; i++) (dd[i] as HTMLElement).classList.toggle('on', i === idx);
  }
  // Reset each page's scroll — only on an actual page change, so a snap-back to the same page (a
  // sub-threshold or boundary swipe) doesn't yank a reader's vertical position back to the top.
  function resetScroll(): void {
    const pgs = track.children;
    for (let i = 0; i < pgs.length; i++) (pgs[i] as HTMLElement).scrollTop = 0;
  }
  function go(i: number): void {
    const clamped = Math.max(0, Math.min(n - 1, i));
    if (clamped === idx) {
      sync(); // boundary over-swipe / same dot: snap the dragged track back, keep scroll position
      return;
    }
    idx = clamped;
    haptic(6);
    resetScroll();
    sync();
  }

  // ---- swipe (horizontal-intent lock; vertical scroll of a long page is left to the browser) ----
  let sx = 0, sy = 0, lock: 'x' | 'y' | null = null, dragging = false;
  const SLOP = 10;
  function onDown(e: PointerEvent): void {
    if (e.button != null && e.button > 0) return;
    dragging = true; lock = null; sx = e.clientX; sy = e.clientY;
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }
  function onMove(e: PointerEvent): void {
    if (!dragging) return;
    const dx = e.clientX - sx, dy = e.clientY - sy;
    if (lock == null && (Math.abs(dx) > SLOP || Math.abs(dy) > SLOP)) {
      lock = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (lock === 'x') {
      e.preventDefault();
      const w = viewport.clientWidth || 1;
      const base = (-idx * 100) / n;
      const pct = (dx / w) * (100 / n);
      track.style.transition = 'none';
      track.style.transform = `translateX(${base + pct}%)`;
    }
  }
  function onUp(e: PointerEvent): void {
    if (!dragging) return;
    dragging = false;
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onUp);
    if (lock === 'x') {
      const dx = e.clientX - sx;
      const w = viewport.clientWidth || 1;
      if (dx <= -Math.min(60, w * 0.2)) go(idx + 1);
      else if (dx >= Math.min(60, w * 0.2)) go(idx - 1);
      else sync();
    }
    lock = null;
  }
  viewport.addEventListener('pointerdown', onDown);

  // ---- open / close ----
  function doOpen(): void {
    if (open) return;
    open = true;
    idx = 0;
    root.hidden = false;
    resetScroll();
    sync(false);
    haptic(10);
    opts?.onOpen?.();
  }
  function doClose(): void {
    if (!open) return;
    open = false;
    root.hidden = true;
    haptic(8);
    opts?.onClose?.();
  }
  const close = doClose;

  return {
    open: doOpen,
    close: doClose,
    get isOpen() {
      return open;
    },
  };
}
